import { Action, createReducer, on } from '@ngrx/store';
import { Status } from '../../../models/status';
import {
  clearMileagePlanProfileLookupMessageKeys,
  initializeMileagePlanProfileServiceState,
  mileagePlanProfileSearch,
  mileagePlanProfileSearchByNameAndDob,
  mileagePlanProfileSearchByNameAndDobFailure,
  mileagePlanProfileSearchByNameAndDobSuccess,
  mileagePlanProfileSearchByPhoneNumber,
  mileagePlanProfileSearchByPhoneNumberFailure,
  mileagePlanProfileSearchByPhoneNumberSuccess,
  mileagePlanProfileSearchFailure,
  mileagePlanProfileSearchSuccess,
  resetSimpleMileagePlanProfileLookupState,
  simpleMileagePlanProfileSearch,
  simpleMileagePlanProfileSearchComplete,
} from './mileage-plan-profile-service.actions';
import {
  initialMileagePlanProfileServiceState,
  mileagePlanProfileLookupAdapter,
  mileagePlanProfileSearchAdapter,
  simpleMileagePlanProfileLookupAdapter,
} from './mileage-plan-profile-service.state';

/**
 * Handles all state changes, there is no way to change the state without a reducer AND the reducer never
 * modifies state, it clones the state from the store, changes that cloned state in some way, and then replaces
 * the entire old state with the cloned and modified state.
 */
const featureReducer = createReducer(
  initialMileagePlanProfileServiceState,
  /**
   * Set status to loading
   */
  on(mileagePlanProfileSearch, (state) => ({
    ...state,
    mileagePlanProfileLookupCRUDStatus: Status.LOADING,
    mileagePlanProfileLookupStatus: null,
  })),
  /**
   * Add the mileage plan profile search response and set the status to stable
   */
  on(mileagePlanProfileSearchSuccess, (state, { response }) => ({
    ...state,
    mileagePlanProfileLookupResponse: mileagePlanProfileLookupAdapter.setAll([response.response], state.mileagePlanProfileLookupResponse),
    mileagePlanProfileLookupCRUDStatus: Status.STABLE,
    mileagePlanProfileLookupStatus: response.status,
    mileagePlanProfileLookupMappedMessageKeys: response.mappedMessageKeys,
  })),
  /**
   * Add the error status for the mileage plan profile search and set the status to stable
   */
  on(mileagePlanProfileSearchFailure, (state, { response }) => ({
    ...state,
    mileagePlanProfileLookupCRUDStatus: Status.STABLE,
    mileagePlanProfileLookupStatus: response.status,
    mileagePlanProfileLookupMappedMessageKeys: response.mappedMessageKeys,
  })),
  /**
   * Set status to loading
   */
  on(mileagePlanProfileSearchByPhoneNumber, (state, { searchByPhoneNumberRequest }) => ({
    ...state,
    mileagePlanProfileSearchByPhoneNumberCRUDStatus: Status.LOADING,
    mileagePlanProfileSearchByPhoneNumberStatus: null,
  })),
  /**
   * Add the mileage plan profile search by phone number response and set the status to stable
   */
  on(mileagePlanProfileSearchByPhoneNumberSuccess, (state, { response }) => ({
    ...state,
    mileagePlanProfileSearchByPhoneNumberResponse: mileagePlanProfileSearchAdapter.setOne(
      response.response,
      state.mileagePlanProfileSearchByPhoneNumberResponse
    ),
    mileagePlanProfileSearchByPhoneNumberCRUDStatus: Status.STABLE,
    mileagePlanProfileSearchByPhoneNumberStatus: response.status,
    mileagePlanProfileSearchByPhoneNumberMappedMessageKeys: response.mappedMessageKeys,
  })),
  /**
   * Add the error status for the mileage plan profile search by phone number and set the status to stable
   */
  on(mileagePlanProfileSearchByPhoneNumberFailure, (state, { response }) => ({
    ...state,
    mileagePlanProfileSearchByPhoneNumberCRUDStatus: Status.STABLE,
    mileagePlanProfileSearchByPhoneNumberStatus: response.status,
    mileagePlanProfileSearchByPhoneNumberMappedMessageKeys: response.mappedMessageKeys,
  })),
  /**
   * Set status to loading
   */
  on(mileagePlanProfileSearchByNameAndDob, (state, { searchByNameAndDobRequest }) => ({
    ...state,
    mileagePlanProfileSearchByNameAndDobCRUDStatus: Status.LOADING,
    mileagePlanProfileSearchByNameAndDobStatus: null,
  })),
  /**
   * Add the mileage plan profile search by name and dob response and set the status to stable
   */
  on(mileagePlanProfileSearchByNameAndDobSuccess, (state, { response }) => ({
    ...state,
    mileagePlanProfileSearchByNameAndDobResponse: mileagePlanProfileSearchAdapter.setOne(
      response.response,
      state.mileagePlanProfileSearchByNameAndDobResponse
    ),
    mileagePlanProfileSearchByNameAndDobCRUDStatus: Status.STABLE,
    mileagePlanProfileSearchByNameAndDobStatus: response.status,
    mileagePlanProfileSearchByNameAndDobMappedMessageKeys: response.mappedMessageKeys,
  })),
  /**
   * Add the error status for the mileage plan profile search by name and dob and set the status to stable
   */
  on(mileagePlanProfileSearchByNameAndDobFailure, (state, { response }) => ({
    ...state,
    mileagePlanProfileSearchByNameAndDobCRUDStatus: Status.STABLE,
    mileagePlanProfileSearchByNameAndDobStatus: response.status,
    mileagePlanProfileSearchByNameAndDobMappedMessageKeys: response.mappedMessageKeys,
  })),
  on(clearMileagePlanProfileLookupMessageKeys, (state) => ({
    ...state,
    mileagePlanProfileLookupMappedMessageKeys: [],
  })),
  on(simpleMileagePlanProfileSearch, (state, { featureId }) => ({
    ...state,
    simpleMileagePlanProfileSearchResponse: simpleMileagePlanProfileLookupAdapter.upsertOne(
      { featureId, crudStatus: Status.LOADING, response: null },
      state.simpleMileagePlanProfileSearchResponse
    ),
  })),
  on(simpleMileagePlanProfileSearchComplete, (state, { response }) => ({
    ...state,
    simpleMileagePlanProfileSearchResponse: simpleMileagePlanProfileLookupAdapter.upsertOne(
      { ...response, crudStatus: Status.STABLE },
      state.simpleMileagePlanProfileSearchResponse
    ),
  })),
  on(resetSimpleMileagePlanProfileLookupState, (state) => ({
    ...state,
    simpleMileagePlanProfileSearchResponse: simpleMileagePlanProfileLookupAdapter.removeAll(state.simpleMileagePlanProfileSearchResponse),
  })),
  /**
   * Set the state to the initial value defined in the ticket-service.state file
   */
  on(initializeMileagePlanProfileServiceState, (state) => ({ ...state, ...initialMileagePlanProfileServiceState }))
);

/**
 * Typescript safe way to export the reducer so that it can be imported in modules
 */
export function mileagePlanProfileServiceReducer(state = initialMileagePlanProfileServiceState, action: Action) {
  return featureReducer(state, action);
}
